import React, { useState, useEffect, useCallback } from 'react';
import { Box, Toolbar } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { CarbonOverlay } from '../common';

const CARBON_OVERLAY_KEY = 'energycosmos_carbon_overlay';

export default function MainLayout({ children }) {
  const location = useLocation();

  // Carbon overlay state with localStorage persistence
  const [carbonVisible, setCarbonVisible] = useState(() => {
    const saved = localStorage.getItem(CARBON_OVERLAY_KEY);
    return saved !== null ? saved === 'true' : true; // Default to visible
  });

  useEffect(() => {
    localStorage.setItem(CARBON_OVERLAY_KEY, String(carbonVisible));
  }, [carbonVisible]);

  const handleToggleCarbon = useCallback(() => {
    setCarbonVisible(prev => !prev);
  }, []);

  const handleCloseCarbon = useCallback(() => {
    setCarbonVisible(false);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a1a 0%, #0d0d24 50%, #0a0a1a 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(ellipse 80% 50% at 50% -20%, rgba(0, 212, 255, 0.08), transparent),
            radial-gradient(ellipse 60% 40% at 100% 100%, rgba(0, 255, 136, 0.05), transparent)
          `,
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      <Header carbonVisible={carbonVisible} onToggleCarbon={handleToggleCarbon} />
      <Sidebar />
      <CarbonOverlay visible={carbonVisible} onClose={handleCloseCarbon} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          overflow: 'auto',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Spacer for fixed header */}
        <Toolbar />

        {/* Main content with page transition */}
        <Box
          key={location.pathname}
          sx={{
            flexGrow: 1,
            p: 3,
            animation: 'pageEnter 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            '@keyframes pageEnter': {
              from: {
                opacity: 0,
                transform: 'translateY(12px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {children}
        </Box>

        {/* Footer */}
        <Footer />
      </Box>
    </Box>
  );
}
